import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { track } from "@/lib/analytics";

export type Audience = "broker" | "vendor" | "homeowner";

const STORAGE_KEY = "tribeo_audience";

const isAudience = (v: unknown): v is Audience =>
  v === "broker" || v === "vendor" || v === "homeowner";

type IntentContextValue = {
  audience: Audience | null;
  setAudience: (a: Audience | null, source?: string) => void;
};

const IntentContext = createContext<IntentContextValue | undefined>(undefined);

export const IntentProvider = ({ children }: { children: ReactNode }) => {
  const [audience, setAudienceState] = useState<Audience | null>(null);

  useEffect(() => {
    // 1) URL param has priority
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("audience");
      if (isAudience(fromUrl)) {
        localStorage.setItem(STORAGE_KEY, fromUrl);
        setAudienceState(fromUrl);
        track("audience_auto_detected", `url:${fromUrl}`);
        return;
      }
      // 2) Stored choice
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isAudience(stored)) {
        setAudienceState(stored);
        return;
      }
    } catch {
      // ignore (SSR / blocked storage)
    }
  }, []);

  const setAudience = (a: Audience | null, source = "selector") => {
    setAudienceState(a);
    try {
      if (a) {
        localStorage.setItem(STORAGE_KEY, a);
        track("audience_selected", `${source}:${a}`);
      } else {
        localStorage.removeItem(STORAGE_KEY);
        track("audience_reset", source);
      }
    } catch {
      // ignore
    }
  };

  const value = useMemo(() => ({ audience, setAudience }), [audience]);
  return <IntentContext.Provider value={value}>{children}</IntentContext.Provider>;
};

export const useIntent = () => {
  const ctx = useContext(IntentContext);
  if (!ctx) throw new Error("useIntent must be used within IntentProvider");
  return ctx;
};
